import { memo, useCallback, useEffect, useRef, useState } from 'react';

import { type Board } from '@air/api/types';
import { TreeItem, TreeItemButton } from '@air/component-tree';
import { CheckIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import { Tooltip } from '@air/primitive-tooltip';

import { type BoardListItemProps, BoardSearchListItem } from '~/components/BoardSearch/BoardSearchListItem';
import { useLibraryRootBoards } from '~/components/LibraryBeta/hooks/queries/useLibraryRootBoards';
import { useFetchObjectsPermissions } from '~/hooks/useFetchObjectsPermissions';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';

type Library = Required<Board>['library'];

export type LibraryListItemProps = {
  getIsLibraryDisabled?: (library: Library) => { isDisabled: true; message: string } | undefined;
  isLibrarySelected?: (library: Library) => boolean;
  library: Library;
  libraryBoards?: Board[];
  onSelectLibrary?: (library: Library) => void;
  shouldExpandLibraryInsteadOfSelect?: boolean;
  shouldShowLibraryBoards?: (libraryId: Library['id']) => boolean;
} & Pick<
  BoardListItemProps,
  'getIsBoardDisabled' | 'initialBoardId' | 'isBoardSelected' | 'onBoardSelectChange' | 'shouldShowSubBoards'
>;

export const LibraryListItem = memo(
  ({
    getIsLibraryDisabled,
    getIsBoardDisabled,
    initialBoardId,
    isBoardSelected,
    isLibrarySelected,
    library,
    libraryBoards,
    onBoardSelectChange: onBoardClick,
    onSelectLibrary,
    shouldExpandLibraryInsteadOfSelect,
    shouldShowLibraryBoards,
    shouldShowSubBoards,
  }: LibraryListItemProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const { currentWorkspace } = useCurrentWorkspace();
    // biome-ignore lint/correctness/useExhaustiveDependencies: PLA-1724 — pre-existing debt, not introduced by the icon migration
    const toggleExpanded = useCallback(() => {
      setIsExpanded((prevState) => !prevState);
    }, [setIsExpanded]);

    const isSelected = !!isLibrarySelected?.(library);
    const isDisabled = getIsLibraryDisabled?.(library);

    const onClickTitle = useCallback(() => {
      if (shouldExpandLibraryInsteadOfSelect) {
        toggleExpanded();
      } else {
        onSelectLibrary?.(library);
      }
    }, [onSelectLibrary, library, shouldExpandLibraryInsteadOfSelect, toggleExpanded]);

    useEffect(() => {
      if (shouldShowLibraryBoards?.(library.id)) {
        setIsExpanded(true);
      }
    }, [shouldShowLibraryBoards, library.id]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: PLA-1724 — pre-existing debt, not introduced by the icon migration
    useEffect(() => {
      if (isSelected && containerRef.current) {
        containerRef.current.scrollIntoView();
      }
    }, [isSelected, library]);

    // this will be replaced with a proper api call when we have the endpoint
    const {
      data: boards = [],
      isLoadingMore,
      hasMore,
      loadNextPage,
    } = useLibraryRootBoards({
      workspaceId: isExpanded && !libraryBoards ? currentWorkspace?.id : undefined,
      libraryId: library.id,
    });

    useFetchObjectsPermissions({
      objects: {
        boardIds: boards.map((board) => board.id),
      },
    });

    const treeElement = (
      <TreeItemButton className={isDisabled ? 'pointer-events-none opacity-60' : ''} onClick={onClickTitle}>
        {library.title}
      </TreeItemButton>
    );

    return (
      <div data-testid="LIBRARY_LIST_ITEM" data-title={library.title} ref={containerRef}>
        <TreeItem
          data-title={library.title}
          className={isSelected ? 'bg-indigo-1' : 'hover:bg-gray-4'}
          prefix={
            <div
              className="flex size-6 items-center justify-center rounded-4 bg-gray-4 text-12 ring-1 ring-gray-4"
              data-testid="LIBRARY_LIST_ITEM_ICON"
              style={{ backgroundColor: library.color.backgroundHex }}
            >
              {library.icon || '📚'}
            </div>
          }
          suffix={isSelected && <CheckIcon className="flex shrink-0 items-center text-blue-11" size="sm" />}
          open={isExpanded}
          onOpenChange={toggleExpanded}
        >
          {isDisabled ? (
            <Tooltip label={isDisabled.message} side="left" sideOffset={0}>
              <div>{treeElement}</div>
            </Tooltip>
          ) : (
            treeElement
          )}
        </TreeItem>
        {isExpanded && (
          <div className="ml-4">
            {(libraryBoards || boards).map((board) => {
              return (
                <div key={board.id}>
                  <BoardSearchListItem
                    shouldShowSubBoards={shouldShowSubBoards}
                    isBoardSelected={isBoardSelected}
                    onBoardSelectChange={onBoardClick}
                    board={board}
                    getIsBoardDisabled={getIsBoardDisabled}
                    initialBoardId={initialBoardId}
                  />
                </div>
              );
            })}
            {!isLoadingMore && hasMore && (
              // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
              <Button onClick={() => loadNextPage()} className="ml-4 px-4" size="small" appearance="ghost" color="gray">
                Show more
              </Button>
            )}
          </div>
        )}
      </div>
    );
  },
);

LibraryListItem.displayName = 'LibraryListItem';
